import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

const RTC_CONFIGURATION: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

interface LivestreamDisplayProps {
  socket: Socket | null;
  sessionId: string | null;
}

export default function LivestreamDisplay({ socket, sessionId }: LivestreamDisplayProps) {
  const [frame, setFrame] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('Warte auf Verbindung zum Livestream...');
  const [hasVideoTrack, setHasVideoTrack] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const streamerIdRef = useRef<string | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const hasVideoTrackRef = useRef(false);

  useEffect(() => {
    hasVideoTrackRef.current = hasVideoTrack;
  }, [hasVideoTrack]);

  useEffect(() => {
    if (!socket || !sessionId) return;

    const cleanupConnection = (resetVideo: boolean) => {
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;
      remoteStreamRef.current = null;
      streamerIdRef.current = null;
      pendingCandidatesRef.current = [];
      setHasVideoTrack(false);
      setFrame(null);

      if (resetVideo && videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    const flushPendingCandidates = async (peerConnection: RTCPeerConnection) => {
      if (!peerConnection.remoteDescription) return;

      while (pendingCandidatesRef.current.length > 0) {
        const nextCandidate = pendingCandidatesRef.current.shift();
        if (!nextCandidate) continue;
        await peerConnection.addIceCandidate(new RTCIceCandidate(nextCandidate));
      }
    };

    const ensurePeerConnection = (streamerId: string) => {
      if (peerConnectionRef.current && streamerIdRef.current === streamerId) {
        return peerConnectionRef.current;
      }

      cleanupConnection(false);

      const peerConnection = new RTCPeerConnection(RTC_CONFIGURATION);
      peerConnectionRef.current = peerConnection;
      streamerIdRef.current = streamerId;
      remoteStreamRef.current = new MediaStream();

      peerConnection.addTransceiver('video', { direction: 'recvonly' });
      peerConnection.addTransceiver('audio', { direction: 'recvonly' });

      peerConnection.ontrack = (event) => {
        const remoteStream = remoteStreamRef.current ?? new MediaStream();
        remoteStreamRef.current = remoteStream;

        if (!remoteStream.getTracks().some((track) => track.id === event.track.id)) {
          remoteStream.addTrack(event.track);
        }

        if (videoRef.current) {
          if (videoRef.current.srcObject !== remoteStream) {
            videoRef.current.srcObject = remoteStream;
          }
          void videoRef.current.play().catch(() => undefined);
        }

        if (event.track.kind === 'video') {
          setHasVideoTrack(true);
          setFrame(null);
          setStatusText('Livestream aktiv');
          return;
        }

        setStatusText('Audio verbunden, warte auf Videobild...');
      };

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate || !streamerIdRef.current) return;

        socket.emit('participant:livestream-display-signal', {
          streamerId: streamerIdRef.current,
          candidate: event.candidate.toJSON(),
        });
      };

      peerConnection.onconnectionstatechange = () => {
        const state = peerConnection.connectionState;
        if (state === 'connecting') {
          setStatusText('Verbinde zum Livestream...');
          return;
        }

        if (state === 'connected') {
          setStatusText('Livestream aktiv');
          return;
        }

        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          cleanupConnection(true);
          setStatusText('WebRTC getrennt, nutze Fallback-Bild...');
        }
      };

      return peerConnection;
    };

    const onSignalFromParticipant = async (payload: { streamerId?: string; description?: RTCSessionDescriptionInit | null; candidate?: RTCIceCandidateInit | null }) => {
      const streamerId = String(payload?.streamerId || '').trim();
      if (!streamerId) return;

      try {
        let peerConnection = ensurePeerConnection(streamerId);

        if (payload.description) {
          if (payload.description.type === 'offer' && peerConnection.signalingState !== 'stable') {
            cleanupConnection(false);
            peerConnection = ensurePeerConnection(streamerId);
          }

          await peerConnection.setRemoteDescription(new RTCSessionDescription(payload.description));
          await flushPendingCandidates(peerConnection);

          if (payload.description.type === 'offer') {
            const answer = await peerConnection.createAnswer();
            await peerConnection.setLocalDescription(answer);

            socket.emit('participant:livestream-display-signal', {
              streamerId,
              description: answer,
            });
          }
        }

        if (payload.candidate) {
          if (!peerConnection.remoteDescription) {
            pendingCandidatesRef.current.push(payload.candidate);
          } else {
            await peerConnection.addIceCandidate(new RTCIceCandidate(payload.candidate));
          }
        }
      } catch (error) {
        console.error('Error handling livestream signal on display:', error);
        setStatusText('WebRTC fehlgeschlagen, warte auf Fallback-Bild...');
      }
    };

    const onFrame = (payload: { frame?: string }) => {
      const nextFrame = typeof payload?.frame === 'string' ? payload.frame : '';
      if (!nextFrame) return;
      setFrame(nextFrame);
      if (!hasVideoTrackRef.current) {
        setStatusText('Fallback-Bild aktiv');
      }
    };

    const onLivestreamState = (payload: { enabled?: boolean }) => {
      const enabled = Boolean(payload?.enabled);
      if (!enabled) {
        cleanupConnection(true);
        setStatusText('Live-Stream wurde vom Instructor gestoppt.');
        return;
      }

      // Force reconnect: clean up and re-join to trigger new negotiation
      cleanupConnection(true);
      setStatusText('Live-Stream gestartet. Warte auf Videobild...');
      // Re-emit join to force new offer from publisher
      socket.emit('participant:join-livestream-display', { sessionId }, (result: { success?: boolean; error?: string }) => {
        if (!result?.success) {
          setStatusText(result?.error || 'Livestream-Anzeige konnte nicht gestartet werden.');
          return;
        }
        setStatusText('Verbunden. Warte auf Videobild...');
      });
    };

    socket.on('livestream:signal-from-participant', onSignalFromParticipant);
    socket.on('livestream:frame', onFrame);
    socket.on('livestream:state', onLivestreamState);

    socket.emit('participant:join-livestream-display', { sessionId }, (result: { success?: boolean; error?: string }) => {
      if (!result?.success) {
        setStatusText(result?.error || 'Livestream-Anzeige konnte nicht gestartet werden.');
        return;
      }
      setStatusText('Verbunden. Warte auf Videobild...');
    });

    return () => {
      socket.emit('participant:leave-livestream-display');
      socket.off('livestream:signal-from-participant', onSignalFromParticipant);
      socket.off('livestream:frame', onFrame);
      socket.off('livestream:state', onLivestreamState);
      cleanupConnection(true);
    };
  }, [sessionId, socket]);

  return (
    <div className="min-h-screen bg-dispatch-dark px-4 py-6 text-white">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="text-center">
          <h1 className="text-3xl font-bold">🎥 Participant Live</h1>
          <p className="mt-2 text-sm text-gray-400">Diese Seite zeigt das gleiche Kamerabild wie die Leitstelle.</p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-700 bg-black shadow-xl">
          <div className="relative flex aspect-video w-full items-center justify-center">
            <video ref={videoRef} autoPlay playsInline className="h-full w-full object-contain" />
            {frame && !hasVideoTrack && (
              <img src={frame} alt="Participant Live Stream" className="absolute inset-0 h-full w-full object-contain" />
            )}
            {!hasVideoTrack && (
              <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-gray-300">
                {statusText}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-gray-800 p-4 text-sm text-gray-300">
          <p className="font-semibold text-white">Status</p>
          <p className="mt-1">{statusText}</p>
        </div>
      </div>
    </div>
  );
}
